import { useContext } from "react";

import { PlusCircleOutlined } from "@ant-design/icons";
import { type FormInstance, Tooltip } from "antd";
import { clsx } from "clsx";
import { useDispatch } from "react-redux";

import { RVIcon, RVIconSizeOptions, RVButton, getColor } from "@regal-voice/ui";
import { ConditionsList } from "Pages/audience/Pages/SegmentsPage/Pages/SegmentEditPage/ConditionsList/ConditionsList";
import { setFields } from "Services/state/journeys/JourneyStateSlice";
import {
    ConditionEvent,
    ConditionList,
    DynamicCondition,
    EventProperty,
    JoinType,
    NamedConditionalTypes,
} from "Types/Segment";

import { JourneyFormContext } from "../../../../../shared/Contexts/JourneyFormContext";
import { showContactConditions } from "../../shared";
import { unflattenFields } from "../../Utils/utils";

import type { ConditionalOptions } from "Pages/audience/Pages/SegmentsPage/Pages/SegmentEditPage/ConditionsList/Types";

import styles from "./TriggerFormItems.module.scss";

const conditionalTypes: NamedConditionalTypes = [{ value: "contactAttribute", name: "Contact Attribute" }];

const propertyDefaults: EventProperty = { propertyOperator: "isSet" };
const eventDefaults: ConditionEvent = {
    eventOperator: "isSet",
    filters: [propertyDefaults],
    lastEventOnly: false,
    conditionalType: "contactAttribute",
};
const conditionDefaults: DynamicCondition = {
    joinType: JoinType.ALL,
    conditions: [eventDefaults],
};

const conditionalOptions: ConditionalOptions = {
    dynamicConditions: [conditionDefaults],
    conditionDefaults,
    joinType: JoinType.ALL,
    direction: "vertical",
    groupName: "Contact Attribute",
    hideEventSelector: true,
    conditionalListOptions: { hideJoinSelect: true, hideAddItemButton: false },
    eventListOptions: { hideJoinSelect: true, hideAddItemButton: true, hideConditionSelector: true },
    propertyListOptions: { hideWhereRow: true },
};

export function ContactConditions({
    form,
    formValues,
}: {
    form: FormInstance;
    formValues: Record<string, any>;
}): JSX.Element {
    const { formSubmited } = useContext(JourneyFormContext);
    const dispatch = useDispatch();
    if (
        !showContactConditions(
            formValues.triggerType || formValues.triggerFiltersEvents?.[0]?.conditions[0].conditionalType
        )
    ) {
        return <></>;
    }

    const dynamicConditions = (formValues || { dynamicConditions: [conditionDefaults] }) as ConditionList;

    function handleAddContactFilter() {
        dispatch(
            setFields(
                unflattenFields({
                    ...formValues,
                    triggerFilters: [conditionDefaults],
                })
            )
        );
    }

    const emptyContactFilters =
        formValues.triggerFilters == null ||
        (Array.isArray(formValues.triggerFilters) && formValues.triggerFilters.length === 0);
    return (
        <div style={{ paddingBottom: "16px" }}>
            <div
                className={clsx("flex-row align-center justify-between", styles.zeroPadding)}
                style={{ paddingBottom: "8px" }}
            >
                <h4 className={clsx(styles.rightPadding)}>
                    <b>Filter by Contact Attribute Condition (Optional)</b>
                </h4>
                <Tooltip
                    title="Define contact attribute conditions to further limit who enters the journey."
                    placement="topRight"
                    color={getColor("purple5")}
                >
                    <div className={styles.tooltip}>
                        <RVIcon size={RVIconSizeOptions.SMALL} icon="info-circle" />
                    </div>
                </Tooltip>
            </div>

            {emptyContactFilters ? (
                <RVButton icon={<PlusCircleOutlined />} size="middle" onClick={handleAddContactFilter}>
                    Add Contact Condition
                </RVButton>
            ) : (
                <ConditionsList
                    form={form}
                    data={dynamicConditions}
                    // I don't know why but backend expects 'dynamicConditions' field to be named 'triggerFilters'
                    // and I didn't find type safe solution for getting DynamicCondition from Record<string, any>
                    fieldName="triggerFilters"
                    conditionalTypes={conditionalTypes}
                    conditionalOptions={conditionalOptions}
                    validationEnabled={formSubmited}
                />
            )}
        </div>
    );
}
